import moment from 'moment-timezone';
import PerformanceUtils from 'services/PerformanceUtils';
import { Shift } from 'generated/api';

class CalendarUtils {
  momentToDate(unixDate: number | moment.Moment): Date {
    if (Number.isInteger(unixDate)) unixDate = moment.unix(unixDate as number);
    return (unixDate as moment.Moment).toDate();
  }

  format(unixTime: number, timeFormat: string = 'YYYY-MM-DD', timezone?: string): string {
    return moment
      .unix(unixTime)
      .tz(timezone ?? this.getTargetTimezone())
      .format(timeFormat);
  }

  fromNow(unixTime: number): string {
    return moment.unix(unixTime).tz(this.getTargetTimezone()).fromNow();
  }

  setToHour(date: moment.Moment, hour: number, minute: number = 0): moment.Moment {
    return moment(new Date(date.year(), date.month(), date.date(), hour, minute, 0));
  }

  joinDateAndTime(date: number | undefined, time: number | undefined): number | undefined {
    if (!date || !time) return undefined;

    let dateMoment = moment.unix(date);
    let timeMoment = moment.unix(time);

    dateMoment.hour(timeMoment.hour());
    dateMoment.minute(timeMoment.minute());
    dateMoment.second(timeMoment.second());

    let convertedDate = this.convertFromTargetTimezone(dateMoment);
    if (convertedDate.date() < dateMoment.date()) {
      dateMoment.date(dateMoment.date() + 1);
    }

    return dateMoment.unix();
  }

  convertFromTargetTimezone(
    datetime: moment.Moment,
    target: string = this.getTargetTimezone(),
  ): moment.Moment {
    const m = moment.tz(datetime, target);
    return moment(new Date(m.year(), m.month(), m.date(), m.hour(), m.minute(), 0));
  }

  convertToTargetTimezone(
    date: moment.Moment,
    target: string = this.getTargetTimezone(),
  ): moment.Moment {
    const dateM = moment.tz(date, this.getCurrentTimezone());
    return moment.tz(
      {
        year: dateM.year(),
        month: dateM.month(),
        date: dateM.date(),
        hour: dateM.hour(),
        minute: dateM.minute(),
      },
      target,
    );
  }

  getCurrentTimezone(): string {
    return CURRENT_TIMEZONE;
  }

  getTargetTimezone(): string {
    return TARGET_TIMEZONE;
  }

  isToday(momentDate: number | moment.Moment): boolean {
    if (typeof momentDate === 'number') {
      momentDate = moment.unix(momentDate);
    }
    return this.isSameDay(momentDate, moment());
  }

  isSameDay(momentDate1: moment.Moment, momentDate2: moment.Moment): boolean {
    let format = 'YYYYMMDD';
    const tz = this.getTargetTimezone();
    return momentDate1.clone().tz(tz).format(format) === momentDate2.clone().tz(tz).format(format);
  }

  formatMilitaryTime(militaryTime: number | string, timeFormat: string = 'h:mm A'): string {
    let time = '' + militaryTime;
    if (time.length === 1) time = '0' + time;
    if (time.length === 3) time = '0' + time;
    while (time.length < 4) time = time + '0';
    return moment(time, 'HHmm').format(timeFormat);
  }

  getMilitaryTime(dateUnix: number, timezone?: string): string {
    const date = moment.unix(dateUnix);
    if (timezone) {
      date.tz(timezone);
    }
    return date.format('HHmm');
  }

  militaryTimeToUnix(
    dateUnix: number,
    militaryTime: string | number,
    timezone: string = this.getTargetTimezone(),
  ): number {
    // console.log('t', timezone)
    const MILITARY_FORMAT = 'HHmm';
    militaryTime = militaryTime + '';
    if (militaryTime.length === 1) militaryTime = '0' + militaryTime;
    if (militaryTime.length === 3) militaryTime = '0' + militaryTime;
    while (militaryTime.length < 4) militaryTime = militaryTime + '0';

    let date = moment.unix(dateUnix);

    let militaryMoment = moment(militaryTime, MILITARY_FORMAT);
    date.hour(militaryMoment.hour());
    date.minute(militaryMoment.minute());
    date.second(militaryMoment.second());

    date = this.convertToTargetTimezone(date, timezone);

    return date.unix();
  }

  filterShifts(shifts: Shift[], date: moment.Moment, clinicId?: string): Shift[] {
    const formattedDate = this.format(date.unix(), 'YYYYMMDD');
    const filteredShifts = shifts.filter((shift) => {
      const matchesDate = shift.date_string === formattedDate;
      const matchesClinic = !clinicId || shift.clinic_id === clinicId;
      return matchesDate && matchesClinic;
    });
    return PerformanceUtils.sortBy(filteredShifts, ['physician_id']);
  }

  calculateAgeNumber(
    unixDoB: number,
    unixTime: number = moment().unix(),
    format: moment.unitOfTime.Diff = 'years',
  ): number {
    let birthday = moment.unix(unixDoB);
    let targetDate = moment.unix(unixTime);
    return targetDate.diff(birthday, format);
  }

  getUTCDay(unix: number): number {
    let value = moment.unix(unix).utc();
    value.hour(12);
    return this.convertFromTargetTimezone(value).unix();
  }

  calculateAge(
    unixDoB: number,
    unixTime: number = moment().unix(),
    detailed: boolean = true,
  ): string {
    let age: string;

    let years = this.calculateAgeNumber(unixDoB, unixTime, 'years');
    let months = this.calculateAgeNumber(unixDoB, unixTime, 'months');

    if (months <= 1) {
      let days = this.calculateAgeNumber(unixDoB, unixTime, 'days');
      age = days + ' d';
    } else if (years === 0) {
      age = months + ' mo';
    } else if (years >= 18) {
      age = years + ' yr';
    } else {
      months -= years * 12;
      age = years + ' yr';
      if (detailed) age += ' + ' + months + ' mo';
    }

    return age;
  }

  toSortableDateString = (
    t: number | moment.Moment,
    timezone: string = this.getCurrentTimezone(),
  ): string => {
    if (moment.isMoment(t)) {
      t = t.clone().tz(timezone);
    } else {
      t = moment.unix(t).tz(timezone);
    }
    return t.format('YYYYMMDD');
  };

  isWeekend = (date: moment.Moment): boolean => {
    const dayOfWeek = date.weekday();
    return dayOfWeek === 0 || dayOfWeek === 6;
  };
}

const CURRENT_TIMEZONE = moment.tz.guess();
const TARGET_TIMEZONE = 'America/Toronto';

export default new CalendarUtils();
